// How each graph's values map onto its colours, from the Android ProfileColorScale, so a reading is
// the same colour here as on the app's map and charts.

/** `lowColor` at the `low` value, `highColor` at `high`; for pace, low is the slower, larger number. */
export interface ColorScale {
  low: number;
  high: number;
  lowColor: string;
  highColor: string;
}

export const HEART_RATE_COLORS = { low: '#FDDF00', high: '#DB2B35' };
export const PACE_COLORS = { low: '#2CC2E3', high: '#0B3D91' };
export const CADENCE_COLORS = { low: '#D7B4FF', high: '#5E00B8' };
export const ELEVATION_COLOR = '#FFDF46';

/** Without a floor a calm walk that only spans a few bpm would read as alarmingly red. */
export const MINIMUM_HEART_RATE_SPREAD_BPM = 20;
/** One minute per kilometre, so a steady walk stays light rather than spreading noise across the scale. */
export const MINIMUM_PACE_SPREAD_SECONDS_PER_METER = 0.06;
export const MINIMUM_CADENCE_SPREAD_STEPS_PER_MINUTE = 20;
export const MINIMUM_ELEVATION_SPREAD_METERS = 10;

/** Nearest-rank percentile of already-sorted values. */
export function percentile(fraction: number, sorted: number[]): number | null {
  if (sorted.length === 0) return null;
  return sorted[Math.round((sorted.length - 1) * fraction)];
}

/** Yellow at the lowest reading, red at the highest. */
export function heartRateScale(bpms: number[]): ColorScale | null {
  if (bpms.length === 0) return null;
  const lowest = Math.min(...bpms);
  const span = Math.max(Math.max(...bpms) - lowest, MINIMUM_HEART_RATE_SPREAD_BPM);
  return { low: lowest, high: lowest + span, lowColor: HEART_RATE_COLORS.low, highColor: HEART_RATE_COLORS.high };
}

/** Over the typical range (10th to 90th percentile), so one long stop or a sprint doesn't flatten the rest. */
export function paceScale(secondsPerMeter: number[]): ColorScale | null {
  const sorted = [...secondsPerMeter].sort((a, b) => a - b);
  const fast = percentile(0.1, sorted);
  const slow = percentile(0.9, sorted);
  if (fast === null || slow === null) return null;
  const span = Math.max(slow - fast, MINIMUM_PACE_SPREAD_SECONDS_PER_METER);
  return { low: slow, high: slow - span, lowColor: PACE_COLORS.low, highColor: PACE_COLORS.high };
}

export function cadenceScale(stepsPerMinute: number[]): ColorScale | null {
  const sorted = [...stepsPerMinute].sort((a, b) => a - b);
  const slow = percentile(0.1, sorted);
  const quick = percentile(0.9, sorted);
  if (slow === null || quick === null) return null;
  const span = Math.max(quick - slow, MINIMUM_CADENCE_SPREAD_STEPS_PER_MINUTE);
  return { low: slow, high: slow + span, lowColor: CADENCE_COLORS.low, highColor: CADENCE_COLORS.high };
}

export function withAlpha(hex: string, alpha: number): string {
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

/** Ascent's line takes its colour from how fast height was gained or lost: pale when slow, deep when fast. */
export const CLIMBING_SPEED_COLORS = ['#F3E3B5', '#E8B866', '#D98B4A', '#B4572F'];
export const DESCENDING_SPEED_COLORS = ['#C4CCDD', '#8C9AC0', '#5C72A8'];
export const LEVEL_COLOR = '#E9DEC4';
export const UPHILL_COLOR = '#E8B866';
export const DOWNHILL_COLOR = '#8C9AC0';

/** Metres an hour, so steady climbing reads as one colour rather than spreading noise across the scale. */
export const MINIMUM_VERTICAL_SPEED_SPREAD = 200;

export interface SpeedScale {
  low: number;
  high: number;
}

/** Over the typical speeds (10th to 90th percentile), as pace is. */
export function speedScale(speeds: number[]): SpeedScale | null {
  const sorted = [...speeds].sort((a, b) => a - b);
  const low = percentile(0.1, sorted);
  const high = percentile(0.9, sorted);
  if (low === null || high === null) return null;
  return { low, high: Math.max(high, low + MINIMUM_VERTICAL_SPEED_SPREAD) };
}

/** `fraction` of the way along evenly spaced `colors`. */
export function rampColor(colors: string[], fraction: number): string {
  const position = Math.min(Math.max(fraction, 0), 1) * (colors.length - 1);
  const index = Math.min(Math.floor(position), colors.length - 2);
  const [from, to] = [colors[index], colors[index + 1]].map((hex) => parseInt(hex.slice(1), 16));
  const share = position - index;
  const channel = (shift: number) =>
    Math.round(((from >> shift) & 255) + (((to >> shift) & 255) - ((from >> shift) & 255)) * share);
  return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}
